import { motion } from "framer-motion";
import { History, CheckCircle, RotateCcw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Event {
  id: string;
  title: string;
  status: string;
  yes_pool: number;
  no_pool: number;
  total_bids: number;
  is_platform_event: boolean;
  deadline: string;
  created_at: string;
  outcome: boolean | null;
}

interface AdminResolvedEventsProps {
  resolvedEvents: Event[];
  isLoading: boolean;
  reResolvingEventId: string | null;
  onReResolve: (eventId: string, newOutcome: boolean) => void;
}

const AdminResolvedEvents = ({
  resolvedEvents,
  isLoading,
  reResolvingEventId,
  onReResolve,
}: AdminResolvedEventsProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-6"
    >
      <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
        <History size={20} className="text-primary" />
        Resolved Events ({resolvedEvents.length})
      </h2>
      <p className="text-sm text-muted-foreground mb-6">
        Change the outcome of resolved events to redistribute coins. Use this to correct errors.
      </p>

      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : resolvedEvents.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle size={48} className="mx-auto mb-4 text-muted-foreground/50" />
            <p>No resolved events yet</p>
          </div>
        ) : (
          resolvedEvents.map((event) => (
            <div
              key={event.id}
              className="p-3 sm:p-4 rounded-lg bg-muted/30 border border-border/50"
            >
              <div className="flex flex-col gap-3">
                {/* Badges Row */}
                <div className="flex items-center gap-2 flex-wrap">
                  {event.is_platform_event && (
                    <span className="px-2 py-0.5 text-xs bg-primary/20 text-primary rounded">
                      PLATFORM
                    </span>
                  )}
                  <span className="px-2 py-0.5 text-xs bg-muted text-muted-foreground rounded">
                    RESOLVED
                  </span>
                  <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                    event.outcome === true 
                      ? "bg-success/20 text-success" 
                      : "bg-destructive/20 text-destructive"
                  }`}>
                    {event.outcome === true ? "YES" : "NO"}
                  </span>
                </div>

                {/* Event Info */}
                <div>
                  <p className="font-medium text-foreground text-sm sm:text-base line-clamp-2">{event.title}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                    Pool: {(event.yes_pool + event.no_pool).toLocaleString()} DC | {event.total_bids} predictions
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    YES: {event.yes_pool.toLocaleString()} DC | NO: {event.no_pool.toLocaleString()} DC
                  </p>
                </div>

                {/* Actions Row */}
                <div className="flex items-center justify-between pt-2 border-t border-border/30">
                  <p className="text-xs text-muted-foreground">Change outcome to:</p>
                  <div className="flex gap-2">
                    <Button
                      variant={event.outcome === true ? "outline" : "success"}
                      size="sm"
                      onClick={() => onReResolve(event.id, true)}
                      disabled={reResolvingEventId === event.id || event.outcome === true}
                      title="Change to YES"
                      className="h-8 px-3"
                    >
                      {reResolvingEventId === event.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <>
                          <RotateCcw size={14} className="mr-1" />
                          YES
                        </>
                      )}
                    </Button>
                    <Button
                      variant={event.outcome === false ? "outline" : "danger"}
                      size="sm"
                      onClick={() => onReResolve(event.id, false)}
                      disabled={reResolvingEventId === event.id || event.outcome === false}
                      title="Change to NO"
                      className="h-8 px-3"
                    >
                      {reResolvingEventId === event.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <>
                          <RotateCcw size={14} className="mr-1" />
                          NO
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </motion.div>
  );
};

export default AdminResolvedEvents;
